import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '../../lib/hooks/usePageTitle.js';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDate, formatRelative } from '../../lib/formatters.js';
import { AiProposalCard } from './components/AiProposalCard.js';
import { ClaimEvidencePanel } from './components/ClaimEvidencePanel.js';
import { ClaimFindingsPanel } from './components/ClaimFindingsPanel.js';
import { ClaimCommentsThread } from './components/ClaimCommentsThread.js';
import { DecisionActionBar } from './components/DecisionActionBar.js';
import { CrossReferencePanel } from './components/CrossReferencePanel.js';
import { SourceBadge } from '../../components/ui/SourceBadge.js';

interface EvidenceItem {
  id: string;
  evidenceType: string;
  fileName: string;
  mimeType?: string | null;
  extractionStatus: string;
  extractedFields?: Record<string, { value: unknown; confidence: number }> | null;
  extractionConfidence?: string | null;
  isHandwritten: boolean;
  ocrText?: string | null;
}

interface Check {
  checkType: string;
  outcome: string;
  confidence: number;
  reasoning: string;
}

interface Comment {
  id: string;
  authorType: string;
  authorId: string | null;
  body: string;
  isSystemMessage: boolean;
  createdAt: string;
  authorName?: string;
}

interface AiProposal {
  id: string;
  proposalType: string;
  proposalStatus: string;
  proposedPayload: {
    verdict?: string;
    confidence?: number;
    reasoning?: string;
    recommendedDecision?: string;
    recommendedAmount?: number | null;
    checks?: Check[];
  } | null;
  reasoning: string | null;
  confidence: number | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewerNotes: string | null;
}

interface Decision {
  stepNumber: number;
  decision: string;
  approvedAmount?: string | null;
  isoCurrencyCode?: string | null;
  comments?: string | null;
  decidedByType: string;
  decidedAt: string;
  acceptedAiProposal: boolean;
}

interface OutletScope {
  outletId: string;
  outlet: { name: string; city?: string | null };
}

interface SourceInstrument {
  type: 'credit_memo' | 'scheme' | null;
  label: string;
  id: string;
  sourceType?: string | null;
}

interface IterationProposal {
  id: string;
  verdict: string;
  recommendedAmount: number | null;
  reasoning: string;
  confidence: number;
  generatedAt: string;
}

interface ClaimReviewDetail {
  id: string;
  claimNumber: string;
  claimType: string;
  status: string;
  claimedAmount: string;
  isoCurrencyCode: string;
  verificationConfidence: string | null;
  verificationSummary: {
    verdict?: string;
    reasoning?: string;
    bundleReasoning?: string;
    recommendedAmount?: number | null;
    checks?: Check[];
  } | null;
  computedEntitlement: string | null;
  approvedAmount: string | null;
  periodStart: string;
  periodEnd: string;
  submittedAt: string | null;
  notes: string | null;
  slaTargetSettleAt: string | null;
  slaBreachedAt: string | null;
  incompleteDocsOverride: boolean;
  incompleteDocsJustification: string | null;
  incompleteDocsOverrideAt: string | null;
  incompleteDocsMissingSlots: string[] | null;
  preSubmissionIterations: number;
  deviationPercentage?: string | null;
  deviationPolicyOutcome?: string | null;
  distributorOrg: { name: string };
  submittedByName?: string;
  evidence: EvidenceItem[];
  verificationChecks: Check[];
  decisions: Decision[];
  outletScopes: OutletScope[];
  comments: Comment[];
  aiProposal: AiProposal | null;
  sourceInstrument: SourceInstrument | null;
  anomalyFlags: Array<{ id: string; flagType: string; severity: string; description: string }>;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600',
  SUBMITTED: 'bg-copper-soft text-copper-deep',
  AI_PROCESSING: 'bg-copper-soft text-copper',
  AWAITING_REVIEW: 'bg-copper-soft text-copper-deep',
  UNDER_REVIEW: 'bg-copper-soft text-copper-deep',
  AUTO_APPROVED: 'bg-success/10 text-success',
  APPROVED: 'bg-success/10 text-success',
  PARTIALLY_APPROVED: 'bg-success/10 text-success',
  REJECTED: 'bg-danger/10 text-danger',
  PAYMENT_QUEUED: 'bg-success/10 text-success',
  PAID: 'bg-success/10 text-success',
  DISPUTED: 'bg-danger/10 text-danger',
  CANCELLED: 'bg-slate-100 text-slate-500',
};

function SlaTag({ slaTargetSettleAt }: { slaTargetSettleAt: string | null }) {
  const { t } = useTranslation();
  if (!slaTargetSettleAt) return null;
  const msUntil = new Date(slaTargetSettleAt).getTime() - Date.now();
  const daysUntil = Math.ceil(msUntil / (1000 * 60 * 60 * 24));
  if (daysUntil > 5) return null;
  const breached = daysUntil < 0;

  return (
    <span
      className={[
        'text-xs font-semibold px-2 py-0.5 rounded-sm',
        breached ? 'bg-danger text-white' : 'bg-danger/10 text-danger',
      ].join(' ')}
    >
      {breached
        ? t('approver.sla.breached', { defaultValue: 'SLA Breached' })
        : t('approver.sla.risk', {
            days: daysUntil,
            defaultValue: `SLA: ${daysUntil}d left`,
          })}
    </span>
  );
}

export function ClaimReviewPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  usePageTitle('approver.review.title');

  const [claim, setClaim] = useState<ClaimReviewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [iterationHistory, setIterationHistory] = useState<IterationProposal[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [expandedProposalId, setExpandedProposalId] = useState<string | null>(null);
  const iterationFetchedRef = useRef(false);
  const viewFiredRef = useRef(false);
  const viewStartTimeRef = useRef<number | null>(null);

  // Override auto-approval state
  type OverrideDecision = 'APPROVE' | 'PARTIAL_APPROVE' | 'REJECT' | 'REQUEST_MORE_EVIDENCE' | 'SEND_TO_REVIEW';
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideDecision, setOverrideDecision] = useState<OverrideDecision>('SEND_TO_REVIEW');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideApprovedAmount, setOverrideApprovedAmount] = useState('');
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [overrideError, setOverrideError] = useState<string | null>(null);

  function closeOverrideModal() {
    setOverrideModalOpen(false);
    setOverrideReason('');
    setOverrideDecision('SEND_TO_REVIEW');
    setOverrideApprovedAmount('');
    setOverrideError(null);
  }

  async function handleOverrideSubmit() {
    if (!id || overrideReason.length < 10) return;
    if (overrideDecision === 'PARTIAL_APPROVE' && !overrideApprovedAmount) return;
    setOverrideSubmitting(true);
    setOverrideError(null);
    try {
      await api.post<unknown>(`/approver/claims/${id}/override-auto-approval`, {
        overrideDecision,
        reason: overrideReason,
        ...(overrideDecision === 'PARTIAL_APPROVE' && overrideApprovedAmount
          ? { approvedAmount: Number(overrideApprovedAmount) }
          : {}),
      });
      closeOverrideModal();
      fetchClaim();
    } catch (err) {
      setOverrideError(err instanceof Error ? err.message : t('errors.internalServerError'));
    } finally {
      setOverrideSubmitting(false);
    }
  }

  const fetchClaim = useCallback(() => {
    if (!id) return;
    setLoading(true);
    api.get<ClaimReviewDetail>(`/approver/claims/${id}`)
      .then((data) => { setClaim(data); })
      .catch(() => { setClaim(null); })
      .finally(() => { setLoading(false); });
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    api.get<ClaimReviewDetail>(`/approver/claims/${id}`)
      .then((data) => { if (!cancelled) { setClaim(data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setClaim(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!historyExpanded || iterationFetchedRef.current || !id) return;
    iterationFetchedRef.current = true;
    setLoadingHistory(true);
    api.get<{ proposals: IterationProposal[] }>(`/approver/claims/${id}/iteration-history`)
      .then((r) => setIterationHistory(r.proposals))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, [historyExpanded, id]);

  // Implicit VIEWED signal — fire once when AI proposal first loads
  useEffect(() => {
    if (!claim?.aiProposal || viewFiredRef.current || !id) return;
    viewFiredRef.current = true;
    viewStartTimeRef.current = Date.now();
    api.post<unknown>(`/approver/claims/${id}/proposal-viewed`, {}).catch(() => {});
  }, [claim?.aiProposal, id]);

  // TIME_SPENT signal — fire on unmount if the proposal was seen for ≥5 seconds
  useEffect(() => {
    return () => {
      if (!viewStartTimeRef.current || !id) return;
      const timeOnPageMs = Date.now() - viewStartTimeRef.current;
      if (timeOnPageMs >= 5000) {
        api.post<unknown>(`/approver/claims/${id}/proposal-viewed`, { timeOnPageMs }).catch(() => {});
      }
    };
  }, [id]);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 max-w-7xl mx-auto">
        <div className="h-8 w-48 bg-slate-100 rounded" />
        <div className="h-4 w-96 bg-slate-100 rounded" />
        <div className="grid grid-cols-12 gap-6 mt-6">
          <div className="col-span-3 h-64 bg-slate-100 rounded" />
          <div className="col-span-6 h-64 bg-slate-100 rounded" />
          <div className="col-span-3 h-64 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  if (!claim) {
    return (
      <div className="text-center py-16">
        <p className="text-danger text-sm">{t('errors.notFound')}</p>
        <button
          type="button"
          onClick={() => navigate('/approver/queue')}
          className="mt-4 text-xs text-copper hover:underline"
        >
          ← {t('approver.queue.title', { defaultValue: 'Approval Queue' })}
        </button>
      </div>
    );
  }

  // Build AiProposalCard props from aiProposal
  const aiProposal = claim.aiProposal;
  const aiChecks: Check[] =
    aiProposal?.proposedPayload?.checks ??
    claim.verificationChecks.map((c) => ({ ...c, confidence: Number(c.confidence) }));

  const aiVerdict =
    aiProposal?.proposedPayload?.verdict ??
    claim.verificationSummary?.verdict ??
    'NEEDS_REVIEW';

  const aiConfidence =
    aiProposal?.confidence ??
    (claim.verificationConfidence != null ? Number(claim.verificationConfidence) : 0);

  const aiReasoning =
    aiProposal?.reasoning ??
    aiProposal?.proposedPayload?.reasoning ??
    claim.verificationSummary?.bundleReasoning ??
    claim.verificationSummary?.reasoning ??
    '';

  const aiRecommendedDecision =
    aiProposal?.proposedPayload?.recommendedDecision ?? 'NEEDS_REVIEW';

  const aiRecommendedAmount =
    aiProposal?.proposedPayload?.recommendedAmount ??
    claim.verificationSummary?.recommendedAmount ??
    null;

  const hasAiData = aiVerdict !== 'NEEDS_REVIEW' || aiReasoning || aiChecks.length > 0;

  return (
    <div className="max-w-[1400px] mx-auto space-y-4">
      {/* Auto-approved system banner */}
      {claim.status === 'AUTO_APPROVED' && (() => {
        const autoApproveDecision = claim.decisions.find((d) => d.decision === 'AUTO_APPROVE');
        const autoApprovedAt = autoApproveDecision?.decidedAt ?? claim.submittedAt;
        return (
          <div className="flex items-center justify-between gap-4 p-3 rounded border border-success/30 bg-success/5">
            <p className="text-sm text-success font-medium">
              {t('approver.review.autoApprovedBanner', {
                timestamp: autoApprovedAt ? formatDate(autoApprovedAt) : '—',
                defaultValue: `Auto-approved by system at ${autoApprovedAt ? formatDate(autoApprovedAt) : '—'}. No action required, but you can override if needed.`,
              })}
            </p>
            <button
              type="button"
              onClick={() => setOverrideModalOpen(true)}
              className="shrink-0 px-3 py-1.5 border border-danger text-danger text-xs font-semibold rounded hover:bg-danger/5 transition-colors"
            >
              {t('approver.review.overrideButton', { defaultValue: 'Override Auto-Approval' })}
            </button>
          </div>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ================================================================
            LEFT COLUMN — Claim summary
        ================================================================ */}
        <div className="lg:col-span-3 space-y-5">
          {/* Back + header */}
          <div>
            <button
              type="button"
              onClick={() => navigate('/approver/queue')}
              className="text-xs text-slate-500 hover:text-copper mb-3 transition-colors block"
            >
              ← {t('approver.queue.title', { defaultValue: 'Approval Queue' })}
            </button>

            <div className="flex items-start gap-2 flex-wrap">
              <h1 className="font-serif italic text-2xl text-ink leading-tight">
                {claim.claimNumber}
              </h1>
              <span
                className={[
                  'text-xs font-semibold px-2 py-0.5 rounded-sm self-center',
                  STATUS_COLORS[claim.status] ?? 'bg-slate-100 text-slate-600',
                ].join(' ')}
              >
                {t(`wizard.status.${claim.status}`, { defaultValue: claim.status })}
              </span>
              <SlaTag slaTargetSettleAt={claim.slaTargetSettleAt} />
              {claim.preSubmissionIterations > 1 && (
                <span className="text-xs font-mono px-2 py-0.5 rounded-sm self-center bg-copper-soft text-copper-deep border border-copper/30">
                  {t('approver.iterationPill', { n: claim.preSubmissionIterations })}
                </span>
              )}
            </div>
          </div>

          {/* Summary table */}
          <div className="border border-hairline rounded bg-bone overflow-hidden">
            {[
              {
                label: t('claims.type'),
                value: t(`wizard.claimTypes.${claim.claimType}.label`, {
                  defaultValue: claim.claimType,
                }),
              },
              {
                label: t('approver.review.distributor', { defaultValue: 'Distributor' }),
                value: claim.distributorOrg.name,
              },
              {
                label: t('approver.review.submittedBy', { defaultValue: 'Submitted by' }),
                value: claim.submittedByName ?? '—',
              },
              {
                label: t('wizard.labels.period'),
                value: `${formatDate(claim.periodStart, 'short')} – ${formatDate(claim.periodEnd, 'short')}`,
              },
              {
                label: t('wizard.labels.claimedAmount'),
                value: formatCurrency(Number(claim.claimedAmount), claim.isoCurrencyCode),
              },
              {
                label: t('wizard.labels.computedEntitlement'),
                value: claim.computedEntitlement
                  ? formatCurrency(Number(claim.computedEntitlement), claim.isoCurrencyCode)
                  : '—',
              },
              ...(claim.approvedAmount
                ? [
                    {
                      label: t('approver.review.approvedAmount', {
                        defaultValue: 'Approved Amount',
                      }),
                      value: formatCurrency(
                        Number(claim.approvedAmount),
                        claim.isoCurrencyCode,
                      ),
                    },
                  ]
                : []),
              {
                label: t('wizard.labels.submittedAt'),
                value: claim.submittedAt ? formatDate(claim.submittedAt) : '—',
              },
            ].map((row) => (
              <div
                key={row.label}
                className="flex border-b border-hairline last:border-0 text-xs"
              >
                <span className="px-3 py-2 text-slate-500 w-2/5 shrink-0 bg-bone-soft border-r border-hairline">
                  {row.label}
                </span>
                <span className="px-3 py-2 font-mono text-ink flex-1 break-all">
                  {row.value}
                </span>
              </div>
            ))}
          </div>

          {/* Source instrument */}
          {claim.sourceInstrument && (
            <div className="border border-hairline rounded bg-bone p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                {t('wizard.labels.sourceInstrument')}
              </p>
              <p className="text-xs font-mono text-ink">{claim.sourceInstrument.label}</p>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs text-slate-400">
                  {claim.sourceInstrument.type === 'credit_memo' ? 'Credit Memo' : 'Scheme'}
                </span>
                {claim.sourceInstrument.sourceType && (
                  <SourceBadge sourceType={claim.sourceInstrument.sourceType} />
                )}
              </div>
            </div>
          )}

          {/* Outlets */}
          {claim.outletScopes.length > 0 && (
            <div className="border border-hairline rounded bg-bone p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                {t('wizard.labels.outlets')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {claim.outletScopes.map((s) => (
                  <span
                    key={s.outletId}
                    className="px-2 py-0.5 bg-bone-soft border border-hairline rounded text-xs"
                  >
                    {s.outlet.name}
                    {s.outlet.city ? ` — ${s.outlet.city}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {claim.notes && (
            <div className="border border-hairline rounded bg-bone p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {t('wizard.labels.notes')}
              </p>
              <p className="text-xs text-slate-600 leading-relaxed">{claim.notes}</p>
            </div>
          )}
        </div>

        {/* ================================================================
            MIDDLE COLUMN — AI + Evidence + Findings + Comments
        ================================================================ */}
        <div className="lg:col-span-6 space-y-6">
          {/* Override callout — render first so approver reads it before AI verdict */}
          {claim.incompleteDocsOverride && (
            <div
              className="rounded bg-copper-soft p-4 space-y-3"
              style={{ borderLeft: '4px solid #A86A2C' }}
            >
              <p className="text-sm font-semibold text-copper-deep">
                {t('approver.overrideTitle')}
              </p>
              {claim.incompleteDocsJustification && (
                <blockquote className="pl-3 border-l-2 border-copper/40 italic text-sm text-ink leading-relaxed">
                  {claim.incompleteDocsJustification}
                </blockquote>
              )}
              {claim.incompleteDocsMissingSlots && claim.incompleteDocsMissingSlots.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-copper-deep mb-1">
                    {t('approver.overrideMissingDocs')}
                  </p>
                  <p className="text-xs text-slate-600">
                    {claim.incompleteDocsMissingSlots
                      .map((key) => t(`wizard.documentSlots.${key}`, { defaultValue: key }))
                      .join(', ')}
                  </p>
                </div>
              )}
              {claim.incompleteDocsOverrideAt && (
                <p className="text-xs text-slate-500">
                  {t('approver.overrideSubmittedAt')}: {formatDate(claim.incompleteDocsOverrideAt)}
                </p>
              )}
            </div>
          )}

          {/* Notes from submitter */}
          {claim.notes && (
            <div className="rounded bg-slate-100 px-4 py-3 space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                {t('approver.review.notesFromSubmitter')}
              </p>
              <p className="text-sm text-slate-700 leading-relaxed">{claim.notes}</p>
            </div>
          )}

          {/* Watchlist — anomaly flags */}
          {claim.anomalyFlags && claim.anomalyFlags.length > 0 && (
            <div className="rounded border border-amber-200 bg-amber-50 p-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                {t('anomaly.watchlistTitle', { defaultValue: 'Watchlist' })} ({claim.anomalyFlags.length})
              </p>
              <div className="space-y-1.5">
                {claim.anomalyFlags.map((flag) => (
                  <div key={flag.id} className="text-xs text-amber-900 flex gap-2">
                    <span className="font-semibold shrink-0">
                      {t(`anomaly.flagTypes.${flag.flagType}`, {
                        defaultValue: flag.flagType.replace(/_/g, ' '),
                      })}:
                    </span>
                    <span>{flag.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Proposal Card */}
          {hasAiData && (
            <div data-tour="ai-proposal-card">
              <div className="flex items-center gap-3 mb-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('wizard.labels.aiSummary')}
                </p>
                {claim.deviationPolicyOutcome && claim.deviationPolicyOutcome !== 'NO_DEVIATION' && (() => {
                  const outcomeColors: Record<string, string> = {
                    WITHIN_TOLERANCE: 'bg-success/10 text-success',
                    TOLERANCE_WITH_DOC: 'bg-copper-soft text-copper-deep',
                    EXCEEDS_TOLERANCE: 'bg-danger/10 text-danger',
                  };
                  const pct = claim.deviationPercentage ? Number(claim.deviationPercentage).toFixed(1) : null;
                  return (
                    <span className={['text-xs font-semibold px-2 py-0.5 rounded-sm', outcomeColors[claim.deviationPolicyOutcome] ?? 'bg-slate-100 text-slate-600'].join(' ')}>
                      {t(`approver.deviation.${claim.deviationPolicyOutcome}`, { defaultValue: claim.deviationPolicyOutcome })}
                      {pct ? ` (${pct}%)` : ''}
                    </span>
                  );
                })()}
              </div>
              <AiProposalCard
                verdict={aiVerdict}
                confidence={aiConfidence}
                reasoning={aiReasoning}
                recommendedDecision={aiRecommendedDecision}
                recommendedAmount={aiRecommendedAmount}
                isoCurrencyCode={claim.isoCurrencyCode}
                checks={aiChecks}
              />
            </div>
          )}


          {/* Iteration History panel */}
          {claim.preSubmissionIterations > 0 && (
            <div className="border border-hairline rounded overflow-hidden">
              <button
                type="button"
                onClick={() => setHistoryExpanded((prev) => !prev)}
                className="w-full px-4 py-3 bg-bone hover:bg-bone-soft transition-colors flex items-center justify-between text-left"
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t('approver.iterationHistory.title')}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono bg-copper-soft text-copper-deep px-1.5 py-0.5 rounded-sm">
                    {claim.preSubmissionIterations}
                  </span>
                  <span className="text-slate-400 text-xs">{historyExpanded ? '▼' : '▶'}</span>
                </div>
              </button>

              {historyExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-hairline">
                  {loadingHistory && (
                    <p className="text-xs text-slate-400 animate-pulse py-2">Loading...</p>
                  )}
                  {!loadingHistory && iterationHistory.length === 0 && (
                    <p className="text-xs text-slate-400 py-2">{t('approver.iterationHistory.empty')}</p>
                  )}
                  {!loadingHistory && iterationHistory.length > 0 && (
                    <div className="space-y-2 mt-1">
                      {iterationHistory.map((p, i) => {
                        const isExpanded = expandedProposalId === p.id;
                        const verdictColor: Record<string, string> = {
                          AUTO_APPROVABLE: 'text-success bg-success/10',
                          NEEDS_REVIEW: 'text-copper bg-copper-soft',
                          INSUFFICIENT_EVIDENCE: 'text-slate-500 bg-slate-100',
                          LIKELY_REJECT: 'text-danger bg-danger/10',
                        };
                        return (
                          <div key={p.id} className="border border-hairline rounded bg-bone text-xs">
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2.5 flex items-start gap-3 hover:bg-bone-soft transition-colors"
                              onClick={() => setExpandedProposalId(isExpanded ? null : p.id)}
                            >
                              <span className="text-slate-400 font-mono shrink-0 mt-0.5">#{i + 1}</span>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                  <span className={['px-1.5 py-0.5 rounded-sm font-semibold text-xs', verdictColor[p.verdict] ?? verdictColor['NEEDS_REVIEW']].join(' ')}>
                                    {t(`wizard.verdicts.${p.verdict}`, { defaultValue: p.verdict })}
                                  </span>
                                  {p.recommendedAmount != null && (
                                    <span className="font-mono text-slate-600">
                                      {formatCurrency(p.recommendedAmount, claim.isoCurrencyCode)}
                                    </span>
                                  )}
                                  <span className="text-slate-400 ml-auto">{formatRelative(p.generatedAt)}</span>
                                </div>
                                <p className={['text-slate-500 leading-relaxed', isExpanded ? '' : 'truncate'].join(' ')}>
                                  {p.reasoning}
                                </p>
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Evidence Panel */}
          {claim.evidence.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                {t('wizard.labels.documents')}
              </p>
              <ClaimEvidencePanel evidence={claim.evidence} claimId={claim.id} />
            </div>
          )}

          {/* Findings Panel */}
          {claim.verificationChecks.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                {t('wizard.labels.verificationChecks')}
              </p>
              <ClaimFindingsPanel
                checks={claim.verificationChecks.map((c) => ({
                  ...c,
                  confidence: Number(c.confidence),
                }))}
              />
            </div>
          )}

          {/* Decisions history */}
          {claim.decisions.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                {t('approver.review.decisions', { defaultValue: 'Decision history' })}
              </p>
              <div className="space-y-2">
                {claim.decisions.map((d, i) => (
                  <div
                    key={i}
                    className={[
                      'p-3 border rounded text-sm',
                      d.decision === 'APPROVE' || d.decision === 'AUTO_APPROVE'
                        ? 'border-success/30 bg-success/5'
                        : d.decision === 'REJECT'
                          ? 'border-danger/30 bg-danger/5'
                          : 'border-hairline bg-bone',
                    ].join(' ')}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="font-semibold text-xs">
                          {t(`approver.decisions.${d.decision}`, { defaultValue: d.decision })}
                        </span>
                        <span className="text-xs text-slate-400 ml-2">{d.decidedByType}</span>
                      </div>
                      {d.approvedAmount != null && (
                        <span className="font-mono text-xs">
                          {formatCurrency(
                            Number(d.approvedAmount),
                            d.isoCurrencyCode ?? claim.isoCurrencyCode,
                          )}
                        </span>
                      )}
                    </div>
                    {d.comments && (
                      <p className="text-xs text-slate-500 mt-1">{d.comments}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">{formatDate(d.decidedAt)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments thread — collapsible */}
          <div className="border border-hairline rounded overflow-hidden">
            <button
              type="button"
              onClick={() => setCommentsExpanded((prev) => !prev)}
              className="w-full px-4 py-3 bg-bone hover:bg-bone-soft transition-colors flex items-center justify-between text-left"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t('approver.comments.title', { defaultValue: 'Comments & Messages' })}
              </span>
              <div className="flex items-center gap-2">
                {claim.comments.length > 0 && (
                  <span className="text-xs bg-copper-soft text-copper-deep px-1.5 py-0.5 rounded-sm font-semibold">
                    {claim.comments.length}
                  </span>
                )}
                <span className="text-slate-400 text-xs">{commentsExpanded ? '▼' : '▶'}</span>
              </div>
            </button>

            {commentsExpanded && (
              <div className="px-4 pb-4 pt-2 border-t border-hairline">
                <ClaimCommentsThread
                  claimId={claim.id}
                  comments={claim.comments}
                  onCommentPosted={fetchClaim}
                />
              </div>
            )}
          </div>
        </div>

        {/* ================================================================
            RIGHT COLUMN — Decision + Cross-reference (sticky)
        ================================================================ */}
        <div className="lg:col-span-3 space-y-5 lg:sticky lg:top-6 self-start">
          {/* Decision bar */}
          <div data-tour="decision-action-bar" className="border border-hairline rounded bg-bone p-4">
            {claim.status === 'AUTO_APPROVED' ? (
              <div className="space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-success">
                  {t('approver.autoApprovedPanel.status', { defaultValue: 'Auto-Approved by System' })}
                </p>
                <p className="text-xs text-slate-500">
                  {t('approver.review.overrideConfirm', {
                    defaultValue: 'Override the system\'s auto-approval? This will route the claim back to manual review and you\'ll need to enter a new decision.',
                  })}
                </p>
                <button
                  type="button"
                  onClick={() => setOverrideModalOpen(true)}
                  className="w-full px-4 py-2 border border-danger text-danger rounded text-sm font-semibold hover:bg-danger/5 transition-colors"
                >
                  {t('approver.review.overrideButton', { defaultValue: 'Override Auto-Approval' })}
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">
                  {t('approver.review.decisionTitle', { defaultValue: 'Approve / Reject' })}
                </p>
                <DecisionActionBar
                  claimId={claim.id}
                  claimedAmount={Number(claim.claimedAmount)}
                  isoCurrencyCode={claim.isoCurrencyCode}
                  currentStatus={claim.status}
                  onDecisionMade={fetchClaim}
                />
              </>
            )}
          </div>

          {/* Cross-reference panel */}
          <div className="border border-hairline rounded bg-bone p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              {t('approver.crossRef.title', { defaultValue: 'Cross-References' })}
            </p>
            <CrossReferencePanel claimId={claim.id} />
          </div>
        </div>
      </div>

      {/* Override auto-approval modal */}
      {overrideModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded border border-hairline shadow-lg max-w-lg w-full mx-4 p-6 space-y-4">
            <h2 className="font-serif italic text-xl text-ink">
              {t('approver.overrideModal.title', { defaultValue: 'Override Auto-Approval' })}
            </h2>
            <p className="text-sm text-slate-600">
              {t('approver.review.overrideConfirm', {
                defaultValue:
                  "Override the system's auto-approval? This will require you to enter a new decision (reject / partial approve / request more evidence).",
              })}
            </p>

            <div className="space-y-4">
              {/* Decision picker */}
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500 block mb-2">
                  {t('approver.overrideModal.decisionLabel', { defaultValue: 'New Decision' })}
                </label>
                <div className="grid grid-cols-1 gap-1.5">
                  {(
                    [
                      { key: 'SEND_TO_REVIEW', label: t('approver.overrideModal.sendToReview', { defaultValue: 'Send to Manual Review' }), cls: 'border-copper text-copper-deep bg-copper-soft' },
                      { key: 'REQUEST_MORE_EVIDENCE', label: t('approver.overrideModal.requestEvidence', { defaultValue: 'Request More Evidence' }), cls: 'border-slate-300 text-slate-700 bg-slate-50' },
                      { key: 'PARTIAL_APPROVE', label: t('approver.overrideModal.partialApprove', { defaultValue: 'Partial Approve' }), cls: 'border-copper text-copper-deep bg-copper-soft' },
                      { key: 'REJECT', label: t('approver.overrideModal.reject', { defaultValue: 'Reject' }), cls: 'border-danger text-danger bg-danger/5' },
                    ] as const
                  ).map(({ key, label, cls }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setOverrideDecision(key)}
                      className={[
                        'w-full px-3 py-2 text-sm font-semibold rounded border transition-colors text-left',
                        overrideDecision === key
                          ? `${cls} ring-2 ring-offset-0 ring-current/30`
                          : 'border-hairline text-slate-500 hover:bg-slate-50',
                      ].join(' ')}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Partial approve amount */}
              {overrideDecision === 'PARTIAL_APPROVE' && (
                <div>
                  <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">
                    {t('approver.actions.approvedAmount', { defaultValue: 'Approved Amount (IDR)' })}
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={overrideApprovedAmount}
                    onChange={(e) => setOverrideApprovedAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-hairline rounded text-sm font-mono bg-bone-soft focus:outline-none focus:border-copper"
                    placeholder={claim?.claimedAmount ?? '0'}
                  />
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="text-xs uppercase tracking-wide text-slate-500 block mb-1">
                  {t('approver.overrideModal.reasonLabel', {
                    defaultValue: 'Reason for override (min 10 characters)',
                  })}
                </label>
                <textarea
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-hairline rounded text-sm bg-bone-soft focus:outline-none focus:border-copper resize-none"
                  placeholder={t('approver.overrideModal.reasonPlaceholder', {
                    defaultValue: 'Explain why you are overriding the auto-approval...',
                  })}
                />
                {overrideReason.length > 0 && overrideReason.length < 10 && (
                  <p className="text-xs text-danger mt-1">
                    {t('approver.overrideModal.reasonTooShort', { defaultValue: 'Minimum 10 characters required.' })}
                  </p>
                )}
              </div>
            </div>

            {overrideError && <p className="text-xs text-danger">{overrideError}</p>}

            <div className="flex gap-2 justify-end pt-2 border-t border-hairline">
              <button
                type="button"
                onClick={closeOverrideModal}
                className="px-4 py-2 border border-hairline rounded text-sm text-slate-500 hover:bg-slate-50 transition-colors"
              >
                {t('approver.actions.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="button"
                disabled={
                  overrideSubmitting ||
                  overrideReason.length < 10 ||
                  (overrideDecision === 'PARTIAL_APPROVE' && !overrideApprovedAmount)
                }
                onClick={() => { void handleOverrideSubmit(); }}
                className={[
                  'px-4 py-2 rounded text-sm font-semibold transition-colors disabled:opacity-50',
                  overrideDecision === 'REJECT'
                    ? 'bg-danger text-white hover:opacity-90'
                    : 'bg-copper text-bone-soft hover:bg-copper-warm',
                ].join(' ')}
              >
                {overrideSubmitting
                  ? t('approver.actions.submitting', { defaultValue: 'Submitting...' })
                  : t('approver.overrideModal.confirmBtn', { defaultValue: 'Confirm Override' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
